import React, { useEffect, useState } from "react";
import "./style.sass";
import { LatexRenderer } from "../../components/latex-renderer";

const sanitizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  let trimmed = url.trim();
  trimmed = trimmed.replace(/(%22|%27|["',\\\/])+$/gi, "").trim();
  trimmed = trimmed.replace(/^(%22|%27|["'\\\/])+/gi, "").trim();
  if (
    !trimmed ||
    trimmed === "null" ||
    trimmed === "undefined" ||
    trimmed === "none" ||
    trimmed === "[]" ||
    trimmed === "{}"
  ) {
    return null;
  }

  // Route question_assets through https://rovianai.com/secure-api/ for valid SSL & HTTP 200
  if (trimmed.includes("question_assets")) {
    const assetPart = trimmed.substring(trimmed.indexOf("question_assets"));
    return `https://rovianai.com/secure-api/${assetPart}`;
  }

  if (trimmed.includes("visal1chatbotvm.southindia.cloudapp.azure.com")) {
    trimmed = trimmed.replace("https://", "http://");
    if (!trimmed.includes(":8001")) {
      trimmed = trimmed.replace(
        "visal1chatbotvm.southindia.cloudapp.azure.com",
        "visal1chatbotvm.southindia.cloudapp.azure.com:8001"
      );
    }
    trimmed = trimmed.replace(":8001/secure-api/", ":8001/");
  }

  return trimmed;
};

const isValidImageUrl = (url) => {
  const sanitized = sanitizeImageUrl(url);
  if (!sanitized) return false;
  return (
    sanitized.startsWith("http://") ||
    sanitized.startsWith("https://") ||
    sanitized.startsWith("data:image/") ||
    sanitized.startsWith("blob:") ||
    sanitized.startsWith("/") ||
    /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(sanitized)
  );
};

const getQuestionImageUrls = (q) => {
  if (!q) return [];
  const rawList = Array.isArray(q.image_urls)
    ? q.image_urls
    : Array.isArray(q.imageUrls)
    ? q.imageUrls
    : Array.isArray(q.images)
    ? q.images
    : [q.image_url || q.imageUrl || q.image || q.diagram || q.question_image];

  return rawList
    .map(sanitizeImageUrl)
    .filter(Boolean)
    .filter(isValidImageUrl);
};

const getOptionImage = (q, key, index) => {
  if (!q) return null;
  const optLetter = String(key).toUpperCase();
  const optLetterLower = String(key).toLowerCase();

  const optImages =
    q.option_images || q.optionImages || q.options_images || q.option_image;

  if (optImages) {
    if (Array.isArray(optImages)) {
      const img = sanitizeImageUrl(optImages[index]);
      if (isValidImageUrl(img)) return img;
    } else if (typeof optImages === "object") {
      const raw =
        optImages[optLetter] ||
        optImages[optLetterLower] ||
        optImages[key] ||
        optImages[index];
      const img = sanitizeImageUrl(raw);
      if (isValidImageUrl(img)) return img;
    }
  }

  const val = q.options?.[key];
  if (typeof val === "string") {
    const img = sanitizeImageUrl(val);
    if (isValidImageUrl(img)) return img;
  }
  if (val && typeof val === "object") {
    const raw = val.image || val.img || val.imageUrl || val.image_url;
    const img = sanitizeImageUrl(raw);
    if (isValidImageUrl(img)) return img;
  }

  return null;
};

const getOptionText = (rawValue) => {
  if (typeof rawValue === "string") {
    const sanitized = sanitizeImageUrl(rawValue);
    if (isValidImageUrl(sanitized)) return "";
    return rawValue.trim();
  }
  if (rawValue && typeof rawValue === "object") {
    return rawValue.text || rawValue.title || "";
  }
  return "";
};

const handleImageError = (e) => {
  let currentSrc = e.target.src || "";
  if (!e.target.dataset.retryAttempted) {
    e.target.dataset.retryAttempted = "true";
    currentSrc = currentSrc.replace(/(%22|%27|["',\\\/])+$/gi, "");
    if (currentSrc.includes("visal1chatbotvm.southindia.cloudapp.azure.com")) {
      let newSrc = currentSrc;
      if (newSrc.startsWith("https://")) {
        newSrc = newSrc.replace("https://", "http://");
      }
      if (!newSrc.includes(":8001")) {
        newSrc = newSrc.replace(
          "visal1chatbotvm.southindia.cloudapp.azure.com",
          "visal1chatbotvm.southindia.cloudapp.azure.com:8001"
        );
      }
      if (newSrc !== currentSrc) {
        e.target.src = newSrc;
        return;
      }
    }
  }
  if (e.target.parentElement && e.target.parentElement.classList.contains("option-img-container")) {
    e.target.parentElement.style.display = "none";
  }
  e.target.style.display = "none";
};

export const AdminQuizPlay = ({
  currentQuestion,
  leaderboard,
}) => {
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (!currentQuestion?.timer_seconds) return;

    setTimeLeft(currentQuestion.timer_seconds);

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [currentQuestion]);

  if (!currentQuestion) {
    return <h2>Waiting for question...</h2>;
  }

  const progress =
    ((currentQuestion.index + 1) /
      currentQuestion.total) *
    100;

  const questionImages = getQuestionImageUrls(currentQuestion);

  const optionsEntries = currentQuestion.options
    ? Array.isArray(currentQuestion.options)
      ? currentQuestion.options.map((val, idx) => [
          String.fromCharCode(65 + idx),
          val,
        ])
      : Object.entries(currentQuestion.options)
    : [];

  return (
    <section className="quiz-play">
      <section className="quiz-layout">
        {/* LEFT */}
        <section className="quiz-card">
          <section className="quiz-header">
            <span className="question-count">
              Q - {currentQuestion.index + 1}/
              {currentQuestion.total}
            </span>

            <span className="timer-quiz">
              00:{String(timeLeft).padStart(2, "0")}
            </span>
          </section>

          {/* Progress */}
          <section className="progress-bar">
            <section
              className="progress-fill"
              style={{
                width: `${
                  (timeLeft /
                    currentQuestion.timer_seconds) *
                  100
                }%`,
              }}
            />
          </section>

          {/* Question */}
          <h3 className="question">
            <LatexRenderer
              text={currentQuestion.question || currentQuestion.question_text || ""}
            />
          </h3>

          {/* Question Diagram Images */}
          {questionImages.length > 0 && (
            <section className="quiz-question-images">
              {questionImages.map((imgUrl, i) => (
                <img
                  key={i}
                  src={imgUrl}
                  alt={`Question Diagram ${i + 1}`}
                  className="quiz-diagram-img"
                  onError={handleImageError}
                />
              ))}
            </section>
          )}

          {/* Options */}
          <section className="options">
            {optionsEntries.map(([key, value], idx) => {
              const optImg = getOptionImage(currentQuestion, key, idx);
              const optText = getOptionText(value);
              const isCorrect =
                currentQuestion.showAnswer &&
                (currentQuestion.correctAnswer === key ||
                  String(currentQuestion.correctAnswer).toUpperCase() ===
                    String(key).toUpperCase());

              return (
                <section
                  key={key}
                  className={`option ${isCorrect ? "correct" : ""}`}
                >
                  <span className="radio"></span>

                  <div className="option-content-wrapper">
                    <div className="option-text-row">
                      <b className="option-key-badge">{key}.</b>
                      {optText && optText.trim() !== "" && (
                        <span className="option-text">
                          <LatexRenderer text={optText} />
                        </span>
                      )}
                    </div>

                    {optImg && (
                      <div className="option-img-container">
                        <img
                          src={optImg}
                          alt=""
                          className="option-diagram-img"
                          onError={handleImageError}
                        />
                      </div>
                    )}
                  </div>
                </section>
              );
            })}
          </section>
        </section>
      </section>

      {/* Bottom */}
      <section className="bottom-bar">
        <section className="bottom-bar-section">
          <section className="progress-section">
            <span className="progress-label">
              Quiz Progress
              <b>
                {" "}
                {currentQuestion.index + 1}/
                {currentQuestion.total}
              </b>
            </span>

            <section className="progress-track">
              <section
                className="progress-bar-fill"
                style={{
                  width: `${progress}%`,
                }}
              />
            </section>
          </section>
        </section>
      </section>
    </section>
  );
};